import React from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  ProgressBar,
  Table,
  Button
} from 'react-bootstrap';
import { Link } from 'react-router-dom';

const DistributorDashboard = () => {
  const stats = [
    {
      label: 'Total Retailers',
      value: '862',
      delta: '+21',
      deltaType: 'up',
      subtitle: 'added this quarter',
      icon: 'bi-shop',
      variant: 'primary'
    },
    {
      label: 'Active Retailers (7d)',
      value: '648',
      delta: '+5.4%',
      deltaType: 'up',
      subtitle: 'service adoption score',
      icon: 'bi-activity',
      variant: 'success'
    },
    {
      label: 'Monthly Volume',
      value: '₹3.18 Cr',
      delta: '+8.9%',
      deltaType: 'up',
      subtitle: 'across cash & digital',
      icon: 'bi-arrow-repeat',
      variant: 'info'
    },
    {
      label: 'Commission Earned',
      value: '₹11.4 L',
      delta: '+6.2%',
      deltaType: 'up',
      subtitle: 'projection till month end',
      icon: 'bi-currency-rupee',
      variant: 'warning'
    }
  ];

  const walletSummary = [
    {
      title: 'Main Wallet',
      value: '₹1,28,400',
      chip: 'Live Balance',
      variant: 'info',
      icon: 'bi-wallet2'
    },
    {
      title: 'Commission Wallet',
      value: '₹24,910',
      chip: 'Releasable',
      variant: 'success',
      icon: 'bi-cash-coin'
    },
    {
      title: 'Security Deposit',
      value: '₹3,50,000',
      chip: 'Locked',
      variant: 'secondary',
      icon: 'bi-safe'
    }
  ];

  const quickActions = [
    {
      title: 'Onboard Retailer',
      description: 'Capture KYC and enable services in one flow',
      icon: 'bi-person-plus-fill',
      to: '/distributor/user-management/retailer'
    },
    {
      title: 'Approve Wallet Request',
      description: 'Review pending top-up requests from retailers',
      icon: 'bi-check-circle-fill',
      to: '/distributor/top-up-request/from-retailer'
    },
    {
      title: 'Raise Top-up to Master',
      description: 'Submit float requirement to master distributor',
      icon: 'bi-arrow-up-right-circle-fill',
      to: '/distributor/top-up-request/send'
    }
  ];

  const networkPerformance = [
    {
      title: 'Retailer Activation',
      current: 82,
      target: 95,
      variant: 'success'
    },
    {
      title: 'Service Utilisation',
      current: 71,
      target: 90,
      variant: 'info'
    },
    {
      title: 'Support SLA',
      current: 88,
      target: 92,
      variant: 'warning'
    }
  ];

  const topRetailers = [
    {
      name: 'Shree Sai Digital',
      region: 'Pune',
      retailers: 4,
      volume: '₹18.6 L',
      score: 95
    },
    {
      name: 'PayPoint Hub',
      region: 'Nagpur',
      retailers: 2,
      volume: '₹15.9 L',
      score: 91
    },
    {
      name: 'Express Mitra',
      region: 'Nashik',
      retailers: 3,
      volume: '₹13.4 L',
      score: 88
    },
    {
      name: 'Reliable Pay Booth',
      region: 'Mumbai',
      retailers: 5,
      volume: '₹12.1 L',
      score: 86
    }
  ];

  const recentActivities = [
    {
      time: 'Today · 09:10 AM',
      title: 'Retailer KYC Approved',
      description: 'Green Mart Services verified and activated BBPS',
      status: 'success'
    },
    {
      time: 'Yesterday · 05:45 PM',
      title: 'Commission Slab Updated',
      description: 'Recharge payout configured for festive campaign',
      status: 'warning'
    },
    {
      time: 'Thu · 02:32 PM',
      title: 'Wallet Top-up Credited',
      description: '₹2,50,000 released by master distributor',
      status: 'info'
    },
    {
      time: 'Wed · 07:55 PM',
      title: 'Support Ticket Escalated',
      description: 'Delayed settlement reported to ops desk',
      status: 'secondary'
    }
  ];

  const servicePerformance = [
    {
      service: 'AEPS',
      share: 38,
      trend: '+4.3%',
      variant: 'primary'
    },
    {
      service: 'BBPS',
      share: 24,
      trend: '+2.7%',
      variant: 'info'
    },
    {
      service: 'DMT',
      share: 20,
      trend: '+1.2%',
      variant: 'success'
    },
    {
      service: 'Recharge',
      share: 14,
      trend: '-1.1%',
      variant: 'warning'
    }
  ];

  return (
    <div className="distributor-dashboard">
      <Container fluid className="py-4">
        <div className="dashboard-header mb-4">
          <div>
            <div className="breadcrumb text-muted">
              <span>Home</span> <span className="mx-2">/</span> <span>Distributor</span>
            </div>
            <h1 className="dashboard-title mb-1">Distributor Command Center</h1>
            <p className="text-muted mb-0">
              Track retailer performance, manage wallet float and stay on top of product adoption.
            </p>
          </div>
          <div className="d-flex flex-column align-items-end gap-2">
            <Badge bg="primary" className="rounded-pill px-3 py-2">
              <i className="bi bi-lightning-charge-fill me-2"></i>
              Sync status: live
            </Badge>
            <Button as={Link} to="/distributor/reports/ledger" variant="outline-primary" size="sm">
              <i className="bi bi-download me-1"></i>
              Download Reports
            </Button>
          </div>
        </div>

        <Row className="g-3">
          {stats.map((item) => (
            <Col key={item.label} xl={3} md={6}>
              <Card className="super-stat-card h-100">
                <Card.Body>
                  <div className="d-flex align-items-start justify-content-between">
                    <div>
                      <p className="stat-label text-muted mb-1">{item.label}</p>
                      <h3 className="stat-value mb-1">{item.value}</h3>
                      <span className={`stat-delta ${item.deltaType === 'up' ? 'text-success' : 'text-danger'}`}>
                        <i className={`bi ${item.deltaType === 'up' ? 'bi-arrow-up-right' : 'bi-arrow-down-right'} me-1`}></i>
                        {item.delta}
                      </span>
                      <p className="stat-subtitle text-muted mb-0">{item.subtitle}</p>
                    </div>
                    <div className={`stat-icon bg-${item.variant}`}>
                      <i className={`bi ${item.icon}`}></i>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <Row className="g-3 mt-1">
          <Col xl={8}>
            <Card className="super-network-card h-100">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-0">Operational Targets</h5>
                  <small className="text-muted">Month-to-date progress</small>
                </div>
                <Badge bg="light" text="dark" className="fw-semibold">
                  Nov 2025 Cycle
                </Badge>
              </Card.Header>
              <Card.Body>
                <Row className="g-4">
                  {networkPerformance.map((item) => (
                    <Col md={4} key={item.title}>
                      <div className="super-progress-item">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="mb-0">{item.title}</h6>
                          <span className="text-muted">{item.current}%</span>
                        </div>
                        <ProgressBar now={item.current} variant={item.variant} className="progress-sm" />
                        <div className="d-flex justify-content-between align-items-center mt-2 text-muted">
                          <small>Goal {item.target}%</small>
                          <small>{item.current >= item.target ? 'On track' : `${item.target - item.current}% gap`}</small>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>

                <div className="d-flex align-items-center justify-content-between bg-light rounded-3 px-3 py-3 mt-3">
                  <div>
                    <h6 className="mb-1">Actionable Insight</h6>
                    <p className="mb-0 text-muted">
                      74 retailers have low BBPS usage. Trigger awareness drive?
                    </p>
                  </div>
                  <Button variant="primary" size="sm">
                    Launch Campaign
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={4}>
            <Card className="super-wallet-card h-100">
              <Card.Header>
                <h5 className="mb-0">Wallet Snapshot</h5>
              </Card.Header>
              <Card.Body className="d-flex flex-column gap-3">
                {walletSummary.map((wallet) => (
                  <div key={wallet.title} className="wallet-item">
                    <div className="wallet-icon">
                      <i className={`bi ${wallet.icon}`}></i>
                    </div>
                    <div className="wallet-content">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <h6 className="mb-1">{wallet.title}</h6>
                          <p className="mb-0 text-muted">{wallet.value}</p>
                        </div>
                        <Badge bg={wallet.variant} className="rounded-pill">
                          {wallet.chip}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="wallet-footer bg-primary-subtle text-primary rounded-3 px-3 py-2">
                  <i className="bi bi-lightbulb-fill me-2"></i>
                  Auto-top up enabled for 12 high volume retailers.
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-3 mt-1">
          <Col lg={8}>
            <Card className="super-table-card h-100">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Top Performing Retailers</h5>
                <Link to="/distributor/reports/retailer-transactions" className="text-primary fw-semibold">
                  View Activity
                  <i className="bi bi-arrow-right-short ms-1"></i>
                </Link>
              </Card.Header>
              <Card.Body className="p-0">
                <Table responsive hover className="mb-0">
                  <thead>
                    <tr>
                      <th>Retailer</th>
                      <th>Region</th>
                      <th>Service Mix</th>
                      <th>Monthly Volume</th>
                      <th className="text-end">Health Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topRetailers.map((retailer) => (
                      <tr key={retailer.name}>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <div className="dist-avatar">
                              {retailer.name
                                .split(' ')
                                .map((word) => word.charAt(0))
                                .join('')
                                .substring(0, 2)}
                            </div>
                            <div>
                              <span className="fw-semibold">{retailer.name}</span>
                              <small className="d-block text-muted">Prime Partner</small>
                            </div>
                          </div>
                        </td>
                        <td>{retailer.region}</td>
                        <td>{retailer.retailers}</td>
                        <td>{retailer.volume}</td>
                        <td className="text-end">
                          <Badge bg={retailer.score > 90 ? 'success' : 'info'} className="px-3 py-2">
                            {retailer.score}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>
            <Card className="super-service-card h-100">
              <Card.Header>
                <h5 className="mb-0">Service Contribution</h5>
                <small className="text-muted">Share of monthly volume</small>
              </Card.Header>
              <Card.Body className="d-flex flex-column gap-3">
                {servicePerformance.map((service) => (
                  <div key={service.service} className="service-item">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0">{service.service}</h6>
                      <span className="text-muted">{service.share}%</span>
                    </div>
                    <ProgressBar now={service.share} variant={service.variant} className="progress-xs" />
                    <div className="d-flex justify-content-between mt-1 text-muted">
                      <small>Contribution</small>
                      <small className={service.trend.startsWith('+') ? 'text-success' : 'text-danger'}>
                        {service.trend}
                      </small>
                    </div>
                  </div>
                ))}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-3 mt-1">
          <Col xl={5}>
            <Card className="super-actions-card h-100">
              <Card.Header>
                <h5 className="mb-0">Quick Actions</h5>
              </Card.Header>
              <Card.Body className="d-flex flex-column gap-3">
                {quickActions.map((action) => (
                  <Link key={action.title} to={action.to} className="quick-action">
                    <div className="action-icon">
                      <i className={`bi ${action.icon}`}></i>
                    </div>
                    <div>
                      <h6 className="mb-1">{action.title}</h6>
                      <p className="mb-0 text-muted">{action.description}</p>
                    </div>
                    <i className="bi bi-arrow-right-short ms-auto"></i>
                  </Link>
                ))}
              </Card.Body>
            </Card>
          </Col>

          <Col xl={7}>
            <Card className="super-timeline-card h-100">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-0">Recent Activities</h5>
                  <small className="text-muted">Updates from last 48 hours</small>
                </div>
                <Button variant="outline-secondary" size="sm">
                  View Logs
                </Button>
              </Card.Header>
              <Card.Body>
                <div className="super-timeline">
                  {recentActivities.map((activity, index) => (
                    <div key={`${activity.title}-${index}`} className="timeline-item">
                      <div className={`timeline-dot ${activity.status}`}></div>
                      <div className="timeline-content">
                        <div className="d-flex justify-content-between align-items-center">
                          <h6 className="mb-0">{activity.title}</h6>
                          <span className="text-muted small">{activity.time}</span>
                        </div>
                        <p className="mb-0 text-muted">{activity.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default DistributorDashboard;

